<template>
  <div class="params-section">
    <!-- Меню добавления параметра по шаблону: триггер - "+" в шапке блока (addMenuTarget), позиция считается от него -->
    <mosta-context-menu
      :visible="addMenuOpen"
      :position="menuPos"
      :min-width="280"
      resizable
      :default-width="320"
      :default-height="360"
      :max-width="480"
      :min-height="240"
      menu-class="params-add-menu-ctx"
      @close="$emit('update:addMenuOpen', false)"
      @content-resize="refitAddMenu"
    >
      <div class="params-add-menu" @click.stop>
        <aux-selector
          v-if="addMenuOpen"
          class="params-add-menu__selector"
          :model-type="parameterTemplatesType"
          :set-id="entityId"
          :preferred-segment="attachmentType"
          :allow-multiple="false"
          limit-height-to-container
          :container-height-px="templateListHeight"
          v-model="templatePick"
          @update:model-value="onTemplatePicked"
          @selector-closed="$emit('update:addMenuOpen', false)"
        />
      </div>
    </mosta-context-menu>

    <app-loader v-if="refreshing" />

    <div class="field-group params-section__body">
      <div
        v-for="(container, pid) in instances"
        :key="containerKey(container)"
        class="params-item"
        :class="{ 'params-item--record': isComplexRow(container) }"
      >
        <div v-if="showRowTitle(container)" class="params-row__title">
          <span class="params-row__title-text">
            {{ container.Instance?.Name }}
          </span>
          <span
            v-if="canEdit && isRemovable(container)"
            class="params-title-action"
            @click="onTitleAction({ action: 'refresh' }, container)"
          >
            <app-tooltip :text="$t('param_action_refresh')">
              <template #activator>
                <app-icon
                  name="refresh"
                  width="14px"
                  color="currentColor"
                  hover-color="#499259"
                  cursor="pointer"
                />
              </template>
            </app-tooltip>
          </span>
          <span
            v-if="canEdit && isRemovable(container)"
            class="params-title-action"
            @click="onTitleAction({ action: 'remove' }, container, $event)"
          >
            <app-tooltip :text="$t('param_action_delete')">
              <template #activator>
                <app-icon
                  name="delete-outline"
                  width="14px"
                  color="currentColor"
                  hover-color="#D05A4F"
                  cursor="pointer"
                />
              </template>
            </app-tooltip>
          </span>
        </div>
        <div class="params-row">
          <div
            class="params-row__value"
            :class="{
              'params-row__value--flat': !isComplexRow(container),
              'params-row__value--single': isSingleValueRow(container),
            }"
          >
            <table
              class="dialog-table"
              :ref="
                (el) => {
                  triggerEls[pid] = el;
                }
              "
            >
              <parameter-inline
                v-if="triggerEls[pid]"
                :is-root="true"
                :editable="canEdit"
                is-table
                :id="'entity-param-' + containerKey(container)"
                v-model="parameters_model[pid]"
                :token-styles="tokenStyles"
                :show-custom="false"
                :value-set-from-instance="true"
                :context-trigger="triggerEls[pid]"
                :create-mode="false"
                :show-field-label="showFieldLabelFor(container)"
                @title-action="onTitleAction($event, container)"
              />
            </table>
          </div>
        </div>
      </div>
    </div>

    <parameter-expand-modal
      v-if="expandedContainer"
      ref="expandModal"
      :id="'entity-param-full-' + containerKey(expandedContainer)"
      :title="expandedTitle"
      :editable="canEdit"
      :token-styles="tokenStyles"
      :show-field-label="showFieldLabelFor(expandedContainer)"
      :model-value="parameters_model[expandedIndex]"
      :loading="refreshing"
      @update:modelValue="onExpandedModelUpdate"
      @refresh="refresh"
    />

    <confirm-action-popup
      v-if="confirmRemoveOpen"
      :title="$t('confirm_delete_parameter_title')"
      question=""
      :question-html="confirmRemoveQuestionHtml"
      :position="confirmRemovePos"
      @confirm="confirmRemove"
      @cancel="closeConfirmRemove"
    />
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from "vue-facing-decorator";
import { reactive } from "vue";
import { parameterStore } from "@/store/parameters/parameter-store";
import { ParameterInstanceContainerType } from "@/models/etc/types";
import { V2ParameterValueType } from "@/models/etc/parameter-types";
import { ParameterInstanceContainer } from "@/models/interfaces/parameters/parameter-models";
import { CommonEntity } from "@/models/interfaces/common";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import MostaContextMenu from "@/components/common/basic/MostaContextMenu.vue";
import ParameterExpandModal from "@/components/main/shared/ParameterExpandModal.vue";
import { AuxSelectorDataType } from "@/components/common/aux-selector/types";
import ConfirmActionPopup from "@/components/common/basic/confirm/ConfirmActionPopup.vue";

@Component({
  name: "ParametersSection",
  emits: ["changed", "update:addMenuOpen"],
  components: {
    AppIcon,
    ConfirmActionPopup,
    AppLoader,
    AppTooltip,
    MostaContextMenu,
    ParameterExpandModal,
  },
  // Обновление зовут из шапки блока на странице, разметка значка живёт там
  expose: ["refresh"],
})
export default class ParametersSection extends Vue {
  // Id сущности (клиент / проект) и её тип (V2ParameterAttachmentType)
  @Prop({ required: true, default: 0 }) readonly entityId!: number;
  @Prop({ required: true, default: 0 }) readonly attachmentType!: number;
  @Prop({ default: 0 }) readonly parameterAccess!: number;
  @Prop({ default: null }) readonly addMenuTarget!: Element | null;
  @Prop({ default: false }) readonly addMenuOpen!: boolean;

  parameter_store: any | undefined = undefined;

  // вхождения параметров сущности и их модели для parameter-inline (по одному контейнеру на модель)
  instances: ParameterInstanceContainer[] = reactive([]);
  parameters_model: ParameterInstanceContainer[][] = reactive([]);
  triggerEls: HTMLElement[] = reactive([]);

  // Окно подтверждения ставится у значка: списковая корзина передаёт точку клика в title-action.
  confirmRemoveOpen = false;
  containerToRemove: ParameterInstanceContainer | null = null;
  confirmRemovePos = { x: 0, y: 0 };

  // Развёрнутая строка адресуется ключом, а не номером. Обновление приводит порядок
  // строк к серверному, и по номеру в открытом окне оказался бы чужой параметр
  expandedKey = "";
  expandedTitle = "";

  refreshing = false;

  get expandedIndex(): number {
    if (!this.expandedKey) {
      return -1;
    }
    return this.instances.findIndex(
      (container) => this.containerKey(container) === this.expandedKey
    );
  }

  get expandedContainer(): ParameterInstanceContainer | null {
    return this.instances[this.expandedIndex] ?? null;
  }

  templatePick: CommonEntity[] = [];
  parameterTemplatesType = AuxSelectorDataType.ParameterTemplates;
  // Высота списка: видимый остаток меню под шапкой поиска и подзаголовком.
  // Стартовое значение совпадает с default-height меню (360) минус эта шапка.
  templateListHeight = 282;
  templateListObs: ResizeObserver | null = null;

  // позиция всплывашки (fixed, в координатах вьюпорта) - считается от кнопки "+"
  menuPos = reactive({ top: "0px", left: "0px" });

  tokenStyles: Record<string, string> = {
    artikul: "tok--link",
    name: "tok--base",
    class: "tok--muted",
  };

  created() {
    this.parameter_store = parameterStore();
  }

  async mounted() {
    await this.loadInstances();
  }

  // право на изменение параметров сущности
  get canEdit(): boolean {
    return this.parameterAccess === 2;
  }

  async loadInstances() {
    const list = await this.fetchInstances();
    this.instances = reactive(list);
    this.parameters_model = reactive(list.map((c) => [c]));
    this.triggerEls = reactive([]);
  }

  private async fetchInstances(): Promise<ParameterInstanceContainer[]> {
    const data = await this.parameter_store.getInstances(
      this.entityId,
      this.attachmentType
    );
    return (data as ParameterInstanceContainer[]) ?? [];
  }

  /**
   * Обновление набора параметров сущности. Отдельного метода на один контейнер в АПИ
   * нет, значок на заголовке списка делает то же, что значок на блоке.
   *
   * Строки сопоставляются по ключу и подменяется только модель строки. Перечитать всё
   * как при первой загрузке нельзя, сброс triggerEls снимает условие, под которым
   * рендерится parameter-inline, и все параметры размонтируются и смонтируются заново
   */
  async refresh(): Promise<void> {
    if (this.refreshing) {
      return;
    }

    this.refreshing = true;
    try {
      const list = await this.fetchInstances();
      if (!list.length && !this.instances.length) {
        return;
      }

      const nextInstances: ParameterInstanceContainer[] = [];
      const nextModels: ParameterInstanceContainer[][] = [];
      const nextTriggers: HTMLElement[] = [];

      list.forEach((container) => {
        const key = this.containerKey(container);
        const at = this.instances.findIndex(
          (existing) => this.containerKey(existing) === key
        );

        nextInstances.push(container);
        nextModels.push([container]);
        // Ссылка на таблицу переезжает вместе со строкой. Потерять её нельзя,
        // под ней стоит условие отрисовки параметра
        nextTriggers.push(at === -1 ? (undefined as any) : this.triggerEls[at]);
      });

      this.instances.splice(0, this.instances.length, ...nextInstances);
      this.parameters_model.splice(
        0,
        this.parameters_model.length,
        ...nextModels
      );
      this.triggerEls.splice(0, this.triggerEls.length, ...nextTriggers);
    } finally {
      this.refreshing = false;
    }
  }

  // Стабильный ключ строки по идентичности контейнера (а не по индексу):
  // при удалении/переупорядочивании Vue иначе патчит строки по позиции и рисует не тот параметр.
  containerKey(container: ParameterInstanceContainer): string {
    if (container.Type === ParameterInstanceContainerType.List && container.ListTitle) {
      return "list_" + container.ListTitle.Id;
    }
    if (container.Instance) {
      return "inst_" + container.Instance.Id;
    }
    return "row_" + this.instances.indexOf(container);
  }

  // Сложные строки - запись или список записей. Только у них оставляем верхнюю границу
  // ячеек dialog-table; у простых значений и простых списков она визуально лишняя.
  isComplexRow(container: ParameterInstanceContainer): boolean {
    if (container.Type === ParameterInstanceContainerType.SingleInstance) {
      return container.Instance?.ValueType === V2ParameterValueType.Record;
    }
    if (container.Type === ParameterInstanceContainerType.List) {
      return (
        container.ListTitle?.Instances?.[0]?.ValueType === V2ParameterValueType.Record
      );
    }
    return false;
  }

  // Списки показывают своё название внутри таблицы (InlineDescriptor). Одиночным
  // параметрам (записи и простые значения) добавляем заголовок с их Name.
  showRowTitle(container: ParameterInstanceContainer): boolean {
    return (
      container.Type === ParameterInstanceContainerType.SingleInstance &&
      !!container.Instance?.Name
    );
  }

  // Строка с единственным простым полем: имя параметра стоит заголовком, а под ним ровно
  // один инпут. Отсюда и отсутствие метки в поле, и выравнивание поля по краю заголовка.
  isSingleValueRow(container: ParameterInstanceContainer): boolean {
    return this.showRowTitle(container) && !this.isComplexRow(container);
  }

  // Метка в поле повторяла бы заголовок строки. У записи метки остаются: они называют
  // поля внутри записи, а не сам параметр.
  showFieldLabelFor(container: ParameterInstanceContainer): boolean {
    return !this.isSingleValueRow(container);
  }

  // Полный режим показывает тот же параметр той же моделью, поэтому правки в окне и в
  // строке видны одновременно и ничего синхронизировать не нужно.
  // Проверка на открепляемость нужна и здесь: у списков корзину рисует InlineDescriptor,
  // которому флаг Unremovable не передаётся.
  onTitleAction(
    payload: { action: string; title?: string; anchor?: { x: number; y: number } },
    container: ParameterInstanceContainer,
    event?: MouseEvent
  ): void {
    // Отдельного обновления одного списка в АПИ нет, значок делает то же,
    // что обновление всего блока
    if (payload.action === "refresh") {
      this.refresh();
      return;
    }

    if (payload.action === "expand") {
      this.expandedKey = this.containerKey(container);
      this.expandedTitle =
        payload.title ||
        container.ListTitle?.Name ||
        container.Instance?.Name ||
        "";
      this.$nextTick(() => (this.$refs.expandModal as any)?.onOpen());
      return;
    }

    if (payload.action !== "remove" || !this.isRemovable(container)) return;

    this.containerToRemove = container;
    this.confirmRemovePos = payload.anchor ?? this.anchorFromEvent(event);
    this.confirmRemoveOpen = true;
  }

  get confirmRemoveQuestionHtml(): string {
    const container = this.containerToRemove;
    const name = container?.ListTitle?.Name || container?.Instance?.Name || "";
    const token = "%%NAME%%";
    const translate = this.$t as (
      key: string,
      values: { name: string }
    ) => string;
    const raw = String(
      translate("confirm_delete_parameter_question", { name: token })
    );
    const [before, after] = raw.split(token);
    return (
      this.escapeHtml(before || "") +
      `<span style="font-weight:600">${this.escapeHtml(name)}</span>` +
      this.escapeHtml(after || "")
    );
  }

  escapeHtml(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  closeConfirmRemove(): void {
    this.confirmRemoveOpen = false;
    this.containerToRemove = null;
  }

  anchorFromEvent(event?: MouseEvent): { x: number; y: number } {
    const el = event?.currentTarget as HTMLElement | null;
    const rect = el?.getBoundingClientRect?.();
    if (rect) {
      return { x: Math.round(rect.left), y: Math.round(rect.bottom + 2) };
    }
    if (event) return { x: event.clientX, y: event.clientY };
    return {
      x: Math.round(window.innerWidth / 2 - 120),
      y: Math.round(window.innerHeight / 2 - 60),
    };
  }

  onExpandedModelUpdate(value: ParameterInstanceContainer[]): void {
    if (this.expandedIndex === -1) return;

    this.parameters_model[this.expandedIndex] = value;
  }

  confirmRemove(): void {
    const container = this.containerToRemove;
    this.closeConfirmRemove();
    if (container) this.removeContainer(container);
  }

  // вхождение / список удаляемы, если это позволяет флаг Unremovable
  isRemovable(container: ParameterInstanceContainer): boolean {
    if (container.Type === ParameterInstanceContainerType.List) {
      return !!container.ListTitle && !container.ListTitle.Unremovable;
    }
    return !!container.Instance && !container.Instance.Unremovable;
  }

  @Watch("addMenuOpen")
  async onAddMenuOpenChange(open: boolean) {
    if (!open) {
      this.stopTemplateListObserver();
      return;
    }
    this.templatePick = [];
    const el = this.addMenuTarget as HTMLElement | null;
    if (el) await this.fitMenuToViewport(this.menuPos, el, "params-add-menu-ctx");
    await this.$nextTick();
    this.bindTemplateListObserver();
    this.syncTemplateListHeight();
  }

  beforeUnmount() {
    this.stopTemplateListObserver();
  }

  bindTemplateListObserver() {
    this.stopTemplateListObserver();
    const content = this.templateMenuContent();
    if (!content) return;
    this.templateListObs = new ResizeObserver(() => this.syncTemplateListHeight());
    this.templateListObs.observe(content);
  }

  stopTemplateListObserver() {
    this.templateListObs?.disconnect();
    this.templateListObs = null;
  }

  templateMenuContent(): HTMLElement | null {
    return document.querySelector(
      ".params-add-menu-ctx .mosta-context-menu__content"
    ) as HTMLElement | null;
  }

  // Высота списка: clientHeight меню минус шапка поиска и подзаголовок.
  syncTemplateListHeight() {
    const content = this.templateMenuContent();
    if (!content) return;
    const header = content.querySelector(".selector-search-header") as HTMLElement | null;
    const sub = content.querySelector(".selector-subheader") as HTMLElement | null;
    if (!header) return;
    const chrome = this.outerHeight(header) + this.outerHeight(sub);
    const next = Math.max(80, content.clientHeight - chrome);
    if (next !== this.templateListHeight) this.templateListHeight = next;
  }

  outerHeight(el: HTMLElement | null): number {
    if (!el) return 0;
    const style = getComputedStyle(el);
    return (
      el.offsetHeight +
      (parseFloat(style.marginTop) || 0) +
      (parseFloat(style.marginBottom) || 0)
    );
  }

  // размер меню задаётся контентом, поэтому после каждого изменения высоты уточняем позицию и флип
  async refitAddMenu() {
    if (!this.addMenuOpen) return;
    const el = this.addMenuTarget as HTMLElement | null;
    if (el) await this.fitMenuToViewport(this.menuPos, el, "params-add-menu-ctx");
  }

  // Позиционируем всплывашку от кнопки "+": раскрытие вниз, при нехватке места снизу - флип вверх,
  // по горизонтали зажимаем в пределах вьюпорта. Размер меряем после рендера (высота плавает).
  async fitMenuToViewport(
    posRef: { top: string; left: string },
    triggerEl: HTMLElement,
    menuClass: string
  ) {
    const rect = triggerEl.getBoundingClientRect();
    posRef.top = `${rect.bottom + 4}px`;
    posRef.left = `${rect.left}px`;
    await this.$nextTick();
    const el = document.querySelector("." + menuClass) as HTMLElement | null;
    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    const margin = 8;
    const menuRect = el?.getBoundingClientRect();
    const mw = menuRect?.width ?? 240;
    const mh = menuRect?.height ?? 0;
    let top = rect.bottom + 4;
    if (mh && top + mh > vh - margin) {
      const flipped = rect.top - mh - 4;
      top = flipped >= margin ? flipped : Math.max(margin, vh - mh - margin);
    }
    let left = rect.left;
    if (left + mw > vw - margin) {
      left = Math.max(margin, vw - mw - margin);
    }
    posRef.top = `${top}px`;
    posRef.left = `${left}px`;
  }

  onTemplatePicked(items: CommonEntity[]) {
    const tpl = items?.[0];
    if (!tpl?.Id) return;
    this.templatePick = [];
    this.addTemplate(tpl);
  }

  async addTemplate(tpl: CommonEntity) {
    this.$emit("update:addMenuOpen", false);
    const container = await this.parameter_store.addInstance(
      this.entityId,
      this.attachmentType,
      tpl.Id
    );
    if (container) {
      this.instances.push(container as ParameterInstanceContainer);
      this.parameters_model.push([container as ParameterInstanceContainer]);
      this.$emit("changed");
    }
  }

  async removeContainer(container: ParameterInstanceContainer) {
    let ok = false;
    if (container.Type === ParameterInstanceContainerType.List && container.ListTitle) {
      ok = await this.parameter_store.removeInstanceList(container.ListTitle.Id);
    } else if (container.Instance) {
      ok = await this.parameter_store.removeInstance(container.Instance.Id);
    }
    if (ok) {
      // удаляем по ссылке на контейнер - индекс из шаблона мог устареть после переупорядочивания
      const index = this.instances.indexOf(container);
      if (index !== -1) {
        this.instances.splice(index, 1);
        this.parameters_model.splice(index, 1);
        this.triggerEls.splice(index, 1);
      }
      this.$emit("changed");
    }
  }
}
</script>

<style lang="scss" scoped>
/* Спиннер обновления абсолютный во все края, и ему нужен позиционированный предок */
.params-section {
  position: relative;
  overflow: visible;
  padding: 0;
  margin: 0;
}

.params-section__body {
  padding-left: 0;
  padding-right: 0;
}

.params-item {
  display: flex;
  flex-direction: column;
}

.params-item + .params-item::before {
  content: "";
  display: block;
  height: 1px;
  background: #F2F2F2;
  flex-shrink: 0;
}

.params-item + .params-item--record::before {
  content: none;
}

.params-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.params-row__title {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

/* Кегль стоит на самом тексте, а не на строке заголовка: глобальное правило в app.scss
   задаёт размер каждому элементу поимённо через универсальный селектор, и наследование
   от родителя до текста не доходит. */
.params-row__title-text {
  color: #4d4d4d;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
}

.params-title-action {
  display: inline-flex;
  line-height: 0;
  margin-left: 0;
  min-width: 0;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  color: #979797;
  cursor: pointer;
}

.params-item:hover .params-title-action {
  margin-left: 4px;
  max-width: 14px;
  overflow: visible;
  opacity: 1;
  pointer-events: auto;
}

.params-section :deep(.inline-title-action) {
  margin-left: 0;
  min-width: 0;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.params-item:hover :deep(.inline-title-action) {
  margin-left: 4px;
  max-width: 14px;
  overflow: visible;
  opacity: 1;
  pointer-events: auto;
}

.params-title-action :deep(svg path) {
  vector-effect: non-scaling-stroke;
  stroke-width: 1.5;
}

.params-row__value {
  flex: 1 1 0;
  min-width: 0;
  overflow-x: auto;
}

.params-row__value .dialog-table {
  width: max-content;
  min-width: 100%;
  max-width: none;
}

.params-row__value:not(.params-row__value--flat) .dialog-table {
  width: 100%;
  max-width: 100%;
  min-width: 50vw;
}

.params-row__value:not(.params-row__value--flat) :deep(td:first-child:not(.inline-index-cell)) {
  width: auto;
}

.params-row__value--flat :deep(.dialog-table td) {
  border-top: 0;
}

.params-row__value--flat {
  overflow-x: hidden;
}

.params-row__value--flat .dialog-table {
  width: 100%;
  max-width: 100%;
}

.params-row__value--flat :deep(.token) {
  overflow-wrap: anywhere;
}

.params-row__value--single :deep(.dialog-table td) {
  padding-left: 0;
  padding-right: 0;
}

.params-add-menu {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #fff;
}

.params-add-menu__selector {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
}
</style>
